import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useConfirm } from 'material-ui-confirm';
import {
  Avatar,
  ButtonBase,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Stack,
  SxProps,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import moment from 'moment';

import {
  ISection,
  SECTION_TYPES,
  TUpdateCourseSectionAsAdmin,
  deleteCourseSectionByIdAsAdmin,
  getSectionsState,
} from '../../../../../store/V2/SectionSlice';
import { updateCourseSectionAsAdminSavingSlice } from '../../../../../store/V2/SavingSlice';
import { TP_COLORS } from '../../../../../constants';
import { getAuthState } from '../../../../../store/AuthSlice';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../store/UserSlice';
import { useAppSelector } from '../../../../../hooks';
import { checkUserPermission } from '../../../../../helper';
import {
  TSubsectionCreateNewItemRequestData,
  getCompletedStatusData,
  getCountDownColor,
} from './SectionItem';
import EditableTextWithTextField from '../../../../../components/Inputs/EditableTextWithTextField';
import NewSectionButton from './NewSectionButton';
import EditableTextWithDraftEditor from '../../../../../components/Inputs/EditableTextWithDraftEditor';
import ZoomSectionItem from './ZoomSectionItem';
import ResourceSectionItem from './ResourceSectionItem';

type TState = {
  title: string;
  description: string;
};

interface SubSectionItemProps {
  openedSections: string[];
  parentId: string;
  keyName: string;
  collapse: boolean;
  orderNumber: number;
  section: ISection;
  toggleCollapse: (section: ISection) => void;
  onAddNewItem: (data: TSubsectionCreateNewItemRequestData) => void;
  showSectionoptionsModal: (section: ISection) => void;
  onEditResourceItem: (section: ISection) => void;
  onCancelClass: (section: ISection) => void;
  isOneToOne: boolean;
  onRescheduleClass: (section: ISection) => void;
}

const containerStyles: SxProps = {
  backgroundColor: '#F8F9FD',
  borderRadius: '10px',
};

const SubSectionItem: React.FC<SubSectionItemProps> = ({
  keyName,
  orderNumber,
  section,
  collapse,
  toggleCollapse,
  onAddNewItem,
  parentId,
  openedSections,
  showSectionoptionsModal,
  onEditResourceItem,
  onCancelClass,
  isOneToOne,
  onRescheduleClass,
}) => {
  const confirm = useConfirm();
  const dispatch = useDispatch();
  const urlParams = useParams();

  // Store
  const authState = useAppSelector(getAuthState);
  const sectionsState = useAppSelector(getSectionsState);

  // State
  const [state, setState] = useState<TState>({
    title: '',
    description: '',
  });

  const courseId = urlParams.id;
  const subSections = section.sections || [];
  const statusData = getCompletedStatusData(section.startDate, section.endDate);

  useEffect(() => {
    setState({
      title: section.title,
      description: section.description,
    });
  }, [section]);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleDelete = () => {
    confirm({
      title: 'Are you sure?',
      description: 'Do you want to delete this section?',
    }).then(() => {
      dispatch(deleteCourseSectionByIdAsAdmin(section._id));
    });
  };

  const updateCourseDataByAttributes = (
    attributes: Partial<TUpdateCourseSectionAsAdmin>
  ) => {
    if (courseId) {
      const apiData: TUpdateCourseSectionAsAdmin = {
        _id: section._id,
        course: courseId,
        ...attributes,
      };

      dispatch(updateCourseSectionAsAdminSavingSlice(apiData));
    }
  };

  const renderSubSection = (sections: Array<ISection>) => {
    return (
      <>
        <Stack spacing="8px">
          {sections.map((item, index) => {
            const _keyName = `${keyName}-${index}`;
            const _orderNumber = index + 1;

            if (item.type === SECTION_TYPES.SECTION) {
              const _collapse = openedSections.some(
                (itemId) => itemId === item._id
              );

              return (
                <Stack
                  id={_keyName}
                  key={_keyName}
                  sx={{ border: '1px dashed #CBD5E1', borderRadius: '7px' }}
                >
                  <SubSectionItem
                    isOneToOne={isOneToOne}
                    openedSections={openedSections}
                    parentId={parentId}
                    keyName={_keyName}
                    orderNumber={_orderNumber}
                    section={item}
                    collapse={_collapse}
                    onAddNewItem={onAddNewItem}
                    toggleCollapse={toggleCollapse}
                    showSectionoptionsModal={showSectionoptionsModal}
                    onEditResourceItem={onEditResourceItem}
                    onCancelClass={onCancelClass}
                    onRescheduleClass={onRescheduleClass}
                  />
                </Stack>
              );
            }

            if (item.type === SECTION_TYPES.ZOOM) {
              return (
                <Stack id={_keyName} key={_keyName}>
                  <ZoomSectionItem
                    isOneToOne={isOneToOne}
                    orderNumber={_orderNumber}
                    section={item}
                    onCancelClass={onCancelClass}
                    onRescheduleClass={onRescheduleClass}
                  />
                </Stack>
              );
            }

            if (item.type === SECTION_TYPES.RESOURCE) {
              return (
                <Stack id={_keyName} key={_keyName}>
                  <ResourceSectionItem
                    orderNumber={_orderNumber}
                    section={item}
                    onEdit={onEditResourceItem}
                  />
                </Stack>
              );
            }

            return null;
          })}
        </Stack>
      </>
    );
  };

  return (
    <Card elevation={0} sx={containerStyles}>
      <CardContent sx={{ p: '16px' }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" flexGrow={1} mr="15px">
            <Avatar
              sx={{
                width: '36px',
                height: '36px',
                backgroundColor: '#1C9FF7',
                mr: '15px',
              }}
            >
              {orderNumber}
            </Avatar>
            <div style={{ width: '100%' }}>
              <EditableTextWithTextField
                editable={checkUserPermission(authState.user, {
                  adminCourse: [USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT],
                })}
                label={state.title}
                onSubmit={(newTitle) => {
                  updateStateAttributes({
                    title: newTitle,
                  });

                  updateCourseDataByAttributes({
                    title: newTitle,
                  });
                }}
                textFieldProps={{
                  sx: { flexGrow: 1 },
                  InputProps: {
                    sx: { backgroundColor: TP_COLORS.white },
                  },
                }}
                typographyProps={{
                  fontSize: '16px',
                  fontWeight: 600,
                  className: 'ellipsis-text one-line',
                }}
              />
              {statusData.status ? (
                <div>
                  <Typography
                    fontSize="14px"
                    fontWeight={500}
                    color={statusData.color}
                  >
                    {statusData.status}
                  </Typography>
                </div>
              ) : null}
            </div>
          </Stack>
          <Stack direction="row" alignItems="center">
            {section.startDate && moment(section.startDate) > moment() ? (
              <Typography
                variant="subtitle2"
                style={{ minWidth: '150px', textTransform: 'capitalize' }}
                color={getCountDownColor(section.startDate)}
              >
                {moment(section.startDate).fromNow()}
              </Typography>
            ) : null}
            <ButtonBase
              onClick={() => toggleCollapse(section)}
              sx={{
                padding: '8px 6px',
                backgroundColor: '#E2E8F0',
                color: '#334155',
                borderRadius: '5px',
                fontWeight: 600,
                fontSize: '12px',
                minWidth: '72px',
              }}
            >
              {collapse ? 'HIDE' : 'VIEW'}
            </ButtonBase>
            <Tooltip title="Settings" placement="top">
              <IconButton
                onClick={() => showSectionoptionsModal(section)}
                disabled={sectionsState.status === 'loading'}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            {checkUserPermission(authState.user, {
              adminCourse: [USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT],
            }) ? (
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={handleDelete}
                  disabled={sectionsState.status === 'loading'}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
        </Stack>
      </CardContent>
      <Collapse in={collapse}>
        <CardContent sx={{ p: '0 32px' }}>
          <div style={{ marginBottom: '15px' }}>
            <EditableTextWithDraftEditor
              editable={checkUserPermission(authState.user, {
                adminCourse: [USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT],
              })}
              label={state.description}
              editorStyles={{ backgroundColor: '#ffffff' }}
              onSubmit={(newDescription) => {
                updateStateAttributes({
                  description: newDescription,
                });

                updateCourseDataByAttributes({
                  description: newDescription,
                });
              }}
              textContainerSx={{
                '& p': {
                  margin: 0,
                },
              }}
            />
          </div>
          <Stack spacing={2}>
            {renderSubSection(subSections)}
            {checkUserPermission(authState.user, {
              adminCourse: [USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT],
            }) ? (
              <NewSectionButton
                disabled={sectionsState.status === 'loading'}
                onClick={() =>
                  onAddNewItem({
                    parentSection: {
                      _id: section._id,
                      sortingKey: section.sortingKey,
                      nextSortingKey: subSections.length + 1,
                    },
                  })
                }
              />
            ) : null}
          </Stack>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default SubSectionItem;
